import React, { useContext, useEffect, useRef, useState } from 'react';

import styles from './ListaStatusFaturaEmpresa.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

import { UserContext } from '../../UserContext';
import Pagination from '../pagination/Pagination';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';

import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';

function getAnoMesAtual() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${now.getFullYear()}-${month}`; // yyyy-MM
}

function ListaStatusFaturaEmpresa() {
    const { user } = useContext(UserContext);
    const { get } = useHttpClient();
    const { exibirErro } = useNotificacoes();

    const [competencia, setCompetencia] = useState(getAnoMesAtual());

    // ✅ combo empresas (mesmo padrão do CadastroUsuario)
    const [empresas, setEmpresas] = useState([]);
    const [empresaSelecionadaId, setEmpresaSelecionadaId] = useState(user?.empresa?.id || '');

    const [statusEmpresa, setStatusEmpresa] = useState(null);

    // paginação no padrão do projeto (0-based)
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const [loading, setLoading] = useState(false);

    // controla auto-consulta na primeira carga
    const jaCarregou = useRef(false);

    // ✅ carrega empresas no mesmo padrão do CadastroUsuario
    useEffect(() => {
        (async () => {
            try {
                const empresaIdUsuario = user?.empresa?.id;
                if (!empresaIdUsuario) return;

                const lista = (await get(`/hello-sol/empresas/lista/${empresaIdUsuario}`)) || [];
                setEmpresas(Array.isArray(lista) ? lista : []);

                // garante um default selecionado
                const defaultId = (user?.empresa?.id || (Array.isArray(lista) && lista[0]?.id) || '');
                setEmpresaSelecionadaId(String(defaultId));
            } catch (e) {
                console.error(e);
                exibirErro('Falha ao carregar empresas.');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.empresa?.id]);

    // ✅ primeira carga (auto-consulta) — só depois de ter empresaSelecionadaId
    useEffect(() => {
        if (!jaCarregou.current && empresaSelecionadaId) {
            jaCarregou.current = true;
            consultar(0, empresaSelecionadaId, competencia);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresaSelecionadaId]);

    const consultar = async (page = 0, empresaIdParam = empresaSelecionadaId, competenciaParam = competencia) => {
        if (!empresaIdParam) return;

        try {
            setLoading(true);

            const url = `/hello-sol/faturas-status/empresa/${empresaIdParam}?competencia=${competenciaParam}`;
            const data = await get(url);

            // paginação client-side (porque o backend retorna tudo)
            const usinas = Array.isArray(data?.usinas) ? data.usinas : [];
            const total = Math.ceil(usinas.length / pageSize);

            setStatusEmpresa(data);
            setTotalPages(total);
            setCurrentPage(page);
        } catch (e) {
            console.error(e);
            exibirErro('Falha ao consultar o status geral da empresa.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!empresaSelecionadaId) {
            exibirErro('Selecione a empresa.');
            return;
        }

        setCurrentPage(0);
        consultar(0, empresaSelecionadaId, competencia);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            // dados já estão em memória: só muda a página (slice abaixo)
        }
    };

    const usinas = statusEmpresa?.usinas || [];

    // aplica a paginação no array
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    const usinasPaginadas = usinas.slice(startIndex, endIndex);

    return (
        <div className={styles_global.container}>
            <h2>Status Geral de Faturas (Empresa)</h2>

            <form className={styles.filterForm} onSubmit={handleSubmit}>
                <div className={`${styles_content.formField} ${styles.empresaField}`}>
                    <label>Empresa</label>
                    <select
                        value={empresaSelecionadaId}
                        onChange={(e) => {
                            setEmpresaSelecionadaId(e.target.value);
                            setStatusEmpresa(null);
                            setCurrentPage(0);
                            // mantém a regra do projeto: só consulta ao clicar em "Consultar"
                            // (se você quiser auto-consultar ao trocar empresa, eu ajusto)
                        }}
                    >
                        <option value="">Selecione a Empresa</option>
                        {empresas.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                                {emp.nome}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={`${styles_content.formField} ${styles.competenciaField}`}>
                    <label>Competência</label>
                    <input
                        type="month"
                        value={competencia}
                        onChange={(e) => setCompetencia(e.target.value)}
                    />
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {!loading && statusEmpresa && (
                <>
                    <div className={styles.resumoCards}>
                        <div className={styles.cardResumo}>
                            <div className={styles.cardTitulo}>USINAS</div>
                            <div className={styles.cardValor}>{statusEmpresa.totalUsinas}</div>
                        </div>

                        <div className={styles.cardResumo}>
                            <div className={styles.cardTitulo}>TOTAL UCs</div>
                            <div className={styles.cardValor}>{statusEmpresa.totalUnidades}</div>
                        </div>

                        <div className={styles.cardResumo}>
                            <div className={styles.cardTitulo}>GERADAS</div>
                            <div className={styles.cardValor}>{statusEmpresa.totalGeradas}</div>
                            <div className={styles.cardSub}>
                                Manual: {statusEmpresa.totalOrigemManual} • PDF: {statusEmpresa.totalOrigemPdf} • Robô: {statusEmpresa.totalOrigemRobo}
                            </div>
                        </div>

                        <div className={styles.cardResumo}>
                            <div className={styles.cardTitulo}>PENDENTES</div>
                            <div className={styles.cardValor}>{statusEmpresa.totalPendentes}</div>
                        </div>

                        {/*
                        <div className={styles.cardResumo}>
                            <div className={styles.cardTitulo}>PROCESSANDO</div>
                            <div className={styles.cardValor}>{statusEmpresa.totalProcessando}</div>
                        </div>

                        <div className={styles.cardResumo}>
                            <div className={styles.cardTitulo}>ERROS</div>
                            <div className={styles.cardValor}>{statusEmpresa.totalErros}</div>
                        </div>
                        */}
                    </div>

                    {usinas.length > 0 ? (
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                <tr>
                                    <th>USINA</th>
                                    <th>TOTAL UCs</th>
                                    <th>GERADAS</th>
                                    <th>PENDENTES</th>
                                    {/* <th>PROCESSANDO</th> */}
                                    {/* <th>ERROS</th> */}
                                    <th>ORIGEM (M/PDF/R)</th>
                                    <th>AÇÕES</th>
                                </tr>
                                </thead>
                                <tbody>
                                {usinasPaginadas.map((u) => (
                                    <tr key={u.idUsina}>
                                        <td>{u.nomeUsina}</td>
                                        <td>{u.totalUnidades}</td>
                                        <td>{u.totalGeradas}</td>
                                        <td>{u.totalPendentes}</td>
                                        {/* <td>{u.totalProcessando}</td> */}
                                        {/* <td>{u.totalErros}</td> */}
                                        <td>{u.totalOrigemManual}/{u.totalOrigemPdf}/{u.totalOrigemRobo}</td>
                                        <td className={styles.actions}>
                                            <Link
                                                to={`/home/listar-status-faturas?idUsina=${u.idUsina}&competencia=${competencia}`}
                                                className={styles.iconButton}
                                                title="Detalhar status da usina"
                                            >
                                                <FaEye className={styles.icon} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    ) : (
                        <p className={styles_global.noData}>Nenhuma usina encontrada.</p>
                    )}
                </>
            )}

            {!loading && !statusEmpresa && (
                <p className={styles_global.noData}>
                    Selecione a empresa, informe a competência e clique em <b>Consultar</b>.
                </p>
            )}
        </div>
    );
}

export default ListaStatusFaturaEmpresa;
